/**
 * GA4 wrapper. Cookieless mode (PRD §17 decision 7) — sufficient under AU
 * Privacy Act, no cookie banner required.
 *
 * Loaded after first paint to protect LCP. The script tag is injected
 * imperatively in initAnalytics().
 */

import type { CBEvent } from './events';

let initialised = false;

export function initAnalytics(): void {
  if (initialised) return;
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!id) return;
  initialised = true;

  // Load gtag.js script after first paint.
  requestIdleCallback(
    () => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      document.head.appendChild(s);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer!.push(args);
      };
      window.gtag('js', new Date());
      // Cookieless config.
      window.gtag('config', id, {
        client_storage: 'none',
        anonymize_ip: true,
      });
    },
    { timeout: 4000 }
  );
}

export function trackEvent(event: CBEvent): void {
  if (typeof window === 'undefined') return;
  if (!window.gtag) return;
  window.gtag('event', event.name, event.params);
}

// Polyfill for browsers without requestIdleCallback (older Safari).
declare global {
  interface Window {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  }
}

if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  window.requestIdleCallback = (cb: () => void) => window.setTimeout(cb, 1) as unknown as number;
}

function requestIdleCallback(cb: () => void, opts?: { timeout?: number }): number {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    return window.requestIdleCallback(cb, opts);
  }
  return setTimeout(cb, opts?.timeout ?? 1) as unknown as number;
}
